import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  createPluginKey,
  revokePluginKey,
  updatePluginKey,
} from '@sportninja/common/services/account'
import type {
  PluginKey,
  PluginKeyPayload,
} from '@sportninja/common/features/account/apiAccess'

interface UpdateInput {
  pluginKeyId: string
  payload: PluginKeyPayload
}

/**
 * Create / edit / revoke a plugin key. Each refreshes the account's key list.
 * `retry: 0` overrides the global mutation retry: a create that timed out and
 * retried would issue two keys.
 */
export const usePluginKeyMutations = (accountUid: string) => {
  const queryClient = useQueryClient()
  const refreshList = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.accountApiAccess.pluginKeys(accountUid),
    })

  const create = useMutation<PluginKey, Error, PluginKeyPayload>({
    mutationFn: (payload) => createPluginKey(accountUid, payload),
    onSuccess: refreshList,
    retry: 0,
  })

  const update = useMutation<PluginKey, Error, UpdateInput>({
    mutationFn: ({ pluginKeyId, payload }) =>
      updatePluginKey(accountUid, pluginKeyId, payload),
    onSuccess: refreshList,
    retry: 0,
  })

  const revoke = useMutation<void, Error, string>({
    mutationFn: (pluginKeyId) => revokePluginKey(accountUid, pluginKeyId),
    onSuccess: refreshList,
    retry: 0,
  })

  return { create, update, revoke }
}
